import { CreditCard, Plus, Receipt, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useTransactions } from './api'
import { useRequestVoid } from './voids'
import { EDocCreateModal } from './components/EDocCreateModal'
import { Section } from './components/FinanceBits'
import { PaymentFormModal } from './components/PaymentFormModal'
import { PaymentsList } from './components/PaymentsList'
import { TransactionFormModal } from './components/TransactionFormModal'
import { TransactionsList } from './components/TransactionsList'
import { TxRowMenu } from './components/TxRowMenu'
import { FINANCE_WRITE_PERM } from './permissions'
import type { Transaction } from './types'
import { FINANCE_MONTHS, monthLabel, useFinanceLookups } from './ui'
import { ExportMenu } from './components/ExportMenu'
import { exportTransactionsCsv, printTransactions } from './reports'

type KindFilter = 'all' | 'income' | 'expense'
type StatusFilter = 'all' | 'open' | 'paid' | 'missing'

export function TransactionsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const txs = useTransactions()
  const voids = useRequestVoid()
  const { partyName, catName } = useFinanceLookups()
  const [view, setView] = useState<'records' | 'payments'>('records')
  const [kind, setKind] = useState<KindFilter>('all')
  const [params] = useSearchParams()
  const initialStatus = params.get('status')
  const [status, setStatus] = useState<StatusFilter>(initialStatus === 'missing' || initialStatus === 'open' || initialStatus === 'paid' ? initialStatus : 'all')
  const [month, setMonth] = useState('all')
  const [q, setQ] = useState('')
  const [txForm, setTxForm] = useState<{ open: boolean; tx: Transaction | null }>({ open: false, tx: null })
  const [edocFor, setEdocFor] = useState<Transaction | null>(null)
  const [payFor, setPayFor] = useState<{ open: boolean; tx: Transaction | null }>({ open: false, tx: null })
  const canWrite = can(FINANCE_WRITE_PERM)

  const all = txs.data ?? []
  const term = q.trim().toLocaleLowerCase('tr')
  const rows = all.filter(
    (x) =>
      (kind === 'all' || x.kind === kind) &&
      (month === 'all' || x.date.startsWith(month)) &&
      (status === 'all' || (status === 'open' ? x.status !== 'paid' : status === 'paid' ? x.status === 'paid' : !x.hasDocument)) &&
      (!term || `${x.description} ${x.docNo} ${partyName(x.counterpartyId)}`.toLocaleLowerCase('tr').includes(term)),
  )

  // İptal doğrudan yapılmaz: gerekçeli talep açılır, onaycı / firma sahibi onaylayınca uygulanır.
  const onDelete = (tx: Transaction) => voids.request('ledger', tx.id, t('finance.tx.deleteBody', { name: tx.description, party: partyName(tx.counterpartyId) }))

  const { pageRows, pager } = usePagination(rows, [kind, month, status, q])
  const counts = { all: all.length, income: all.filter((x) => x.kind === 'income').length, expense: all.filter((x) => x.kind === 'expense').length }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.tx.title')}
        description={t('finance.tx.subtitle')}
        actions={
          <>
            <ExportMenu
              disabled={!rows.length}
              onCsv={() => exportTransactionsCsv(rows, { partyName, catName })}
              onPdf={() => printTransactions(rows, { partyName, catName }, month === 'all' ? t('finance.reports.allPeriods') : monthLabel(month))}
            />
            {canWrite ? (
            <>
              <Button variant="outline" onClick={() => setPayFor({ open: true, tx: null })}>
                <CreditCard />
                {t('finance.pay.add')}
              </Button>
              <Button onClick={() => setTxForm({ open: true, tx: null })}>
                <Plus />
                {t('finance.tx.add')}
              </Button>
            </>
            ) : null}
          </>
        }
      />

      <div className="space-y-4">
        <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
          <TabsList>
            <TabsTrigger value="records">
              <Receipt />
              {t('finance.tx.records')}
            </TabsTrigger>
            <TabsTrigger value="payments">
              <CreditCard />
              {t('finance.pay.movements')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {view === 'payments' ? (
          <PaymentsList canWrite={canWrite} />
        ) : (
          <Section
            title={t('finance.tx.records')}
            bodyClassName="p-0"
            actions={<span className="text-xs text-muted-foreground">{t('finance.tx.count', { count: rows.length, total: all.length })}</span>}
          >
            <div className="flex flex-col gap-2 border-b p-3 lg:flex-row lg:flex-wrap lg:items-center">
              <Tabs value={kind} onValueChange={(v) => setKind(v as KindFilter)}>
                <TabsList size="sm">
                  {(['all', 'income', 'expense'] as const).map((k) => (
                    <TabsTrigger key={k} value={k}>
                      {t(`finance.tx.filter.${k}`, { count: counts[k] })}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <div className="grid grid-cols-2 gap-2 lg:flex">
                <Select
                  size="sm"
                  className="lg:w-40"
                  value={month}
                  onChange={setMonth}
                  aria-label={t('finance.period')}
                  options={[{ value: 'all', label: t('finance.allPeriods') }, ...[...FINANCE_MONTHS].reverse().map((m) => ({ value: m, label: monthLabel(m) }))]}
                />
                <Select
                  size="sm"
                  className="lg:w-44"
                  value={status}
                  onChange={(v) => setStatus(v as StatusFilter)}
                  aria-label={t('finance.tx.status')}
                  options={(['all', 'open', 'paid', 'missing'] as const).map((s) => ({ value: s, label: t(`finance.tx.statusFilter.${s}`) }))}
                />
              </div>
              <div className="relative min-w-0 flex-1 lg:max-w-xs">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('finance.tx.search')} aria-label={t('finance.tx.search')} className="h-8 pl-8" />
              </div>
            </div>
            {txs.isError ? (
              <ErrorState error={txs.error} onRetry={() => void txs.refetch()} />
            ) : txs.isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <EmptyState icon={Receipt} title={all.length ? t('finance.tx.noMatch') : t('finance.tx.empty')} />
            ) : (
              <>
                <TransactionsList
                  rows={pageRows}
                  canWrite={canWrite}
                  menu={(tx) => (
                    <TxRowMenu
                      tx={tx}
                      onEdit={() => setTxForm({ open: true, tx })}
                      onPay={() => setPayFor({ open: true, tx })}
                      onEDoc={() => setEdocFor(tx)}
                      onDelete={() => void onDelete(tx)}
                    />
                  )}
                />
                <Pager {...pager} />
              </>
            )}
          </Section>
        )}
      </div>

      <TransactionFormModal open={txForm.open} transaction={txForm.tx} onOpenChange={(open) => setTxForm((s) => ({ ...s, open }))} />
      <EDocCreateModal transaction={edocFor} onClose={() => setEdocFor(null)} />
      <PaymentFormModal open={payFor.open} transaction={payFor.tx} onOpenChange={(open) => setPayFor((s) => ({ ...s, open }))} />
    </>
  )
}
